import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CheckCircle, CircleNotch, DownloadSimple, Warning } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// ── Localized copy ────────────────────────────────────────────────────
// Every string on the Preparation screen is picked from the user's saved
// account language. The stages themselves are backend-driven — we never
// fake progress here or animate a rolling percentage.
const STRINGS = {
  "pt-BR": {
    heading: "Preparando sua entrevista personalizada",
    body: "Estamos analisando sua experiência e a vaga-alvo para criar uma entrevista estruturada.",
    format: { quick: "Prática Rápida (6 perguntas)", advanced: "Prática Avançada (10 perguntas)" },
    stages: {
      analyzing_jd: "Analisando a descrição da vaga",
      reviewing_experience: "Revisando a experiência profissional",
      generating_questions: "Gerando perguntas de entrevista",
      validating_questions: "Validando a qualidade e a cobertura das perguntas",
      preparing_interview: "Preparando a entrevista",
    },
    errorTitle: "Não foi possível preparar sua entrevista",
    errorBody: "Algo deu errado ao gerar seu conjunto de perguntas. Tente novamente em alguns instantes.",
    retry: "Tentar novamente",
  },
  en: {
    heading: "Preparing your personalized interview",
    body: "We are reviewing your experience and the target role to create a structured interview.",
    format: { quick: "Quick Practice (6 questions)", advanced: "Advanced Practice (10 questions)" },
    stages: {
      analyzing_jd: "Analyzing the job description",
      reviewing_experience: "Reviewing professional experience",
      generating_questions: "Generating interview questions",
      validating_questions: "Validating question quality and coverage",
      preparing_interview: "Preparing the interview",
    },
    errorTitle: "We couldn't prepare your interview",
    errorBody: "Something went wrong while generating your question set. Please try again in a moment.",
    retry: "Try again",
  },
  "es-419": {
    heading: "Preparando su entrevista personalizada",
    body: "Estamos revisando su experiencia y el puesto objetivo para crear una entrevista estructurada.",
    format: { quick: "Práctica Rápida (6 preguntas)", advanced: "Práctica Avanzada (10 preguntas)" },
    stages: {
      analyzing_jd: "Analizando la descripción del puesto",
      reviewing_experience: "Revisando la experiencia profesional",
      generating_questions: "Generando las preguntas de entrevista",
      validating_questions: "Validando la calidad y la cobertura de las preguntas",
      preparing_interview: "Preparando la entrevista",
    },
    errorTitle: "No pudimos preparar su entrevista",
    errorBody: "Algo salió mal al generar el conjunto de preguntas. Por favor, inténtalo de nuevo en un momento.",
    retry: "Intentar de nuevo",
  },
  "fr-CA": {
    heading: "Préparation de votre entrevue personnalisée",
    body: "Nous analysons votre expérience et le poste visé afin de créer une entrevue structurée.",
    format: { quick: "Pratique Rapide (6 questions)", advanced: "Pratique Avancée (10 questions)" },
    stages: {
      analyzing_jd: "Analyse de la description du poste",
      reviewing_experience: "Revue de l’expérience professionnelle",
      generating_questions: "Génération des questions d’entrevue",
      validating_questions: "Validation de la qualité et de la couverture des questions",
      preparing_interview: "Préparation de l’entrevue",
    },
    errorTitle: "Nous n’avons pas pu préparer votre entrevue",
    errorBody: "Quelque chose a mal tourné pendant la génération des questions. Réessayez dans un instant.",
    retry: "Réessayer",
  },
};

function pickStrings(lang) {
  return STRINGS[lang] || STRINGS.en;
}

// Stage order — used to determine which stages are completed vs pending
// vs currently active. Backend emits exactly these keys, in this order,
// so the screen can drive its checkmarks off the current `prep_stage`.
const STAGE_ORDER = [
  "analyzing_jd",
  "reviewing_experience",
  "generating_questions",
  "validating_questions",
  "preparing_interview",
];

// Hard timeout: if the backend doesn't reach `ready` within this many
// seconds we assume the LLM job failed silently and show the error
// panel with a Try Again button. Generous ceiling for slow LLM days.
const MAX_WAIT_SECONDS = 240;

export default function Prepare() {
  const { sessionId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const t = pickStrings(user?.preferred_language);
  const [stage, setStage] = useState("analyzing_jd");
  const [format, setFormat] = useState(null);
  const [errored, setErrored] = useState(false);
  const [prepError, setPrepError] = useState("");
  const [adminReady, setAdminReady] = useState(false);
  const [downloadingAudit, setDownloadingAudit] = useState(false);
  const [auditError, setAuditError] = useState("");
  const startRef = useRef(Date.now());

  useEffect(() => {
    if (!sessionId) return undefined;
    let stopped = false;
    // The first few polls MAY race the initial POST — the POST needs to
    // finish input validation + allowance gate + placeholder insert before
    // the poll can see anything. We tolerate a short window of 404s so
    // the user doesn't see a false-alarm error state on page load.
    let earlyNotFoundCount = 0;
    const EARLY_NOT_FOUND_TOLERATED = 8; // ~12s at 1500ms poll interval

    const poll = async () => {
      if (stopped) return;
      try {
        const { data } = await api.get(`/sessions/${sessionId}/prep-status`);
        if (stopped) return;
        earlyNotFoundCount = 0;
        if (data.practice_format) setFormat(data.practice_format);
        // Admins pause here after validation so they can export the exact
        // question set before entering the interview. Candidate accounts keep
        // the original automatic handoff with no extra step.
        if (data.status === "in_progress" || data.prep_stage === "ready") {
          if (user?.is_admin) {
            setAdminReady(true);
            return;
          }
          navigate(`/interview/${sessionId}`, { replace: true });
          return;
        }
        if (data.status === "prep_failed") {
          // Surface the short machine tag from prep_error so the user (and
          // debugging humans) can see the actual failure category rather
          // than an opaque "Something went wrong".
          setPrepError(data.prep_error || "");
          setErrored(true);
          return;
        }
        if (data.prep_stage) setStage(data.prep_stage);
      } catch (err) {
        if (err?.response?.status === 404) {
          earlyNotFoundCount += 1;
          if (earlyNotFoundCount < EARLY_NOT_FOUND_TOLERATED) {
            setTimeout(poll, 1500);
            return;
          }
        }
        // Non-recoverable error — surface the retry UI.
        setPrepError(err?.response?.status ? `http_${err.response.status}` : "network");
        setErrored(true);
        return;
      }
      // Global safety net if the backend never flips to ready.
      const elapsed = (Date.now() - startRef.current) / 1000;
      if (elapsed >= MAX_WAIT_SECONDS) {
        setPrepError("timeout");
        setErrored(true);
        return;
      }
      setTimeout(poll, 1500);
    };
    poll();

    return () => { stopped = true; };
  }, [sessionId, navigate, user?.is_admin]);

  const activeIdx = adminReady ? STAGE_ORDER.length : STAGE_ORDER.indexOf(stage);

  const onRetry = () => {
    // Send the candidate back to /upload so they can re-submit. The
    // failed session in the DB will be ignored — a fresh session_id will
    // be minted the next time they submit, so no duplicate is created.
    navigate("/upload", { replace: true });
  };

  const onDownloadAudit = async () => {
    if (!sessionId || downloadingAudit) return;
    setAuditError("");
    setDownloadingAudit(true);
    try {
      const response = await api.get(`/admin/question-audit/${sessionId}.csv`, {
        responseType: "blob",
      });
      const disposition = response.headers?.["content-disposition"] || "";
      const filenameMatch = disposition.match(/filename="([^"]+)"/i);
      const filename = filenameMatch?.[1] || `question-audit_${sessionId}.csv`;
      const url = URL.createObjectURL(response.data);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setAuditError(
        err?.response?.status
          ? `Question audit download failed (HTTP ${err.response.status}).`
          : "Question audit download failed."
      );
    } finally {
      setDownloadingAudit(false);
    }
  };

  const onContinue = () => {
    navigate(`/interview/${sessionId}`, { replace: true });
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center p-4 bg-[color:var(--bg-base)]">
      <div className="w-full max-w-xl rounded-3xl bg-white shadow-xl border border-[color:var(--border-stone)] p-6 sm:p-8">
        <div className="text-xs uppercase tracking-[0.25em] text-[color:var(--brand)] mb-3">
          My English Coach Interview Simulator
        </div>

        {errored ? (
          <div data-testid="prepare-error">
            <div className="flex items-start gap-3">
              <div className="h-12 w-12 shrink-0 rounded-full bg-[color:var(--score-bad)]/10 text-[color:var(--score-bad)] flex items-center justify-center">
                <Warning weight="duotone" size={26} />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-[color:var(--text-primary)] leading-tight"
                    data-testid="prepare-error-title">
                  {t.errorTitle}
                </h1>
                <p className="mt-3 text-sm text-[color:var(--text-secondary)] leading-relaxed"
                   data-testid="prepare-error-body">
                  {t.errorBody}
                </p>
                {prepError && (
                  <p className="mt-2 text-xs font-mono text-[color:var(--text-secondary)]/70"
                     data-testid="prepare-error-reason">
                    Reason: {prepError}
                  </p>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={onRetry}
              className="mt-6 w-full rounded-full bg-[color:var(--brand)] hover:bg-[color:var(--brand-hover)] text-white px-5 py-2.5 text-sm font-medium"
              data-testid="prepare-error-retry"
            >
              {t.retry}
            </button>
          </div>
        ) : (
          <>
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-[color:var(--text-primary)] leading-tight"
                data-testid="prepare-heading">
              {adminReady ? "Question set generated and validated" : t.heading}
            </h1>
            <p className="mt-3 text-sm text-[color:var(--text-secondary)] leading-relaxed"
               data-testid="prepare-body">
              {adminReady
                ? "Admin audit is available before you enter the interview. The export contains the final question set and sanitized generation metadata only."
                : t.body}
            </p>

            {format && (
              <div
                className="mt-4 inline-flex items-center gap-2 rounded-full border border-[color:var(--border-stone)] bg-[color:var(--brand)]/5 px-3 py-1 text-xs uppercase tracking-widest text-[color:var(--text-secondary)]"
                data-testid="prepare-format-badge"
              >
                {format === "advanced" ? t.format.advanced : t.format.quick}
              </div>
            )}

            <ol className="mt-6 space-y-3" data-testid="prepare-stage-list">
              {STAGE_ORDER.map((key, i) => {
                const done = i < activeIdx;
                const active = i === activeIdx;
                return (
                  <li
                    key={key}
                    className="flex items-center gap-3"
                    data-testid={`prepare-stage-${key}`}
                    data-stage-state={done ? "done" : active ? "active" : "pending"}
                  >
                    <span className="shrink-0">
                      {done ? (
                        <CheckCircle weight="fill" size={22} className="text-[color:var(--brand)]" />
                      ) : active ? (
                        <CircleNotch size={22} className="text-[color:var(--brand)] animate-spin" />
                      ) : (
                        <span className="inline-block h-[22px] w-[22px] rounded-full border-2 border-[color:var(--border-stone)]" />
                      )}
                    </span>
                    <span
                      className={
                        done
                          ? "text-sm text-[color:var(--text-secondary)] line-through decoration-[color:var(--border-stone)]"
                          : active
                          ? "text-sm font-medium text-[color:var(--text-primary)]"
                          : "text-sm text-[color:var(--text-secondary)]"
                      }
                    >
                      {t.stages[key]}
                    </span>
                  </li>
                );
              })}
            </ol>

            {adminReady && (
              <div
                className="mt-7 rounded-2xl border border-[color:var(--border-stone)] bg-[color:var(--brand)]/5 p-4"
                data-testid="question-audit-admin-panel"
              >
                <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--brand)]">
                  Admin only
                </div>
                <p className="mt-2 text-sm text-[color:var(--text-secondary)]">
                  Download the generated set now to audit question mix, difficulty, relevance, competency coverage, and validation output.
                </p>
                {auditError && (
                  <p className="mt-2 text-xs text-[color:var(--score-bad)]" role="alert">
                    {auditError}
                  </p>
                )}
                <div className="mt-4 flex flex-col sm:flex-row gap-3">
                  <button
                    type="button"
                    onClick={onDownloadAudit}
                    disabled={downloadingAudit}
                    className="inline-flex items-center justify-center gap-2 rounded-full border border-[color:var(--brand)] px-4 py-2.5 text-sm font-medium text-[color:var(--brand)] disabled:opacity-60"
                    data-testid="question-audit-download"
                  >
                    {downloadingAudit ? (
                      <CircleNotch size={18} className="animate-spin" />
                    ) : (
                      <DownloadSimple size={18} />
                    )}
                    {downloadingAudit ? "Preparing CSV…" : "Download question audit CSV"}
                  </button>
                  <button
                    type="button"
                    onClick={onContinue}
                    className="rounded-full bg-[color:var(--brand)] hover:bg-[color:var(--brand-hover)] text-white px-4 py-2.5 text-sm font-medium"
                    data-testid="question-audit-continue"
                  >
                    Continue to interview
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
